/**
 * Portal de Recepción · backend real (`/api/recepcion/*`).
 * Sustituye la lógica local del store legacy (`ct_boletos_v1`) con
 * llamadas al backend. La interfaz de las funciones legacy se preserva:
 * `getEventosConBoletosEmitidos`, `buscarBoletosPorNombre`,
 * `validarBoletoPorQR`, `validarBoletoManual`.
 */
import { api } from "@/services/api";
import { getReceptionSession } from "@/data/receptionAuthStore";

const KEY_EVENTOS = "ct_recepcion_eventos_v1";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

// Guard del portal de Recepción: mismo contrato de sesión que el layout
// (`getReceptionSession` → `ct_recepcion_session_v1`). NO se reutiliza la
// key administrativa; admin y recepción permanecen separadas.
const haySesionRecepcion = () => !!getReceptionSession();

const readJson = (k, fb) => {
  if (!isBrowser()) return fb;
  try {
    const r = window.localStorage.getItem(k);
    return r ? JSON.parse(r) : fb;
  } catch {
    return fb;
  }
};
const writeJson = (k, v) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};

export const isRecepcionAutenticado = () => haySesionRecepcion();

/* -------------------- Eventos con boletos -------------------- */

const clasificarErrorEventos = (err) => {
  const status = err?.response?.status ?? null;
  const categoria = err?.response?.headers?.["x-auth-rechazo"] || null;
  if (status === 401) return { tipo: "sesion", status, categoria };
  if (status === 403) return { tipo: "permiso", status };
  if (status) return { tipo: "servidor", status };
  return { tipo: "red", status: null };
};

/**
 * Resultado explícito: `{ ok: true, eventos }` sólo tras un 200 con lista;
 * cualquier fallo o respuesta inesperada → `{ ok: false, error }`.
 */
export const hidratarEventosRecepcion = async () => {
  if (!haySesionRecepcion()) {
    return { ok: false, error: { tipo: "sesion", status: null } };
  }
  try {
    const { data } = await api.get("/recepcion/eventos");
    if (!Array.isArray(data)) {
      return { ok: false, error: { tipo: "respuesta", status: 200 } };
    }
    writeJson(KEY_EVENTOS, data);
    return { ok: true, eventos: data };
  } catch (err) {
    return { ok: false, error: clasificarErrorEventos(err) };
  }
};

/** Lectura síncrona desde cache (para reads en useMemo). */
export const getEventosConBoletosEmitidos = () =>
  (readJson(KEY_EVENTOS, []) || []).slice();

/* -------------------- Búsqueda manual -------------------- */

/**
 * Búsqueda tolerante dentro del evento actual.
 *
 * Backend (`GET /api/recepcion/buscar`) resuelve por:
 *   - Nombre / apellido / nombre completo del asistente.
 *   - Número de socio dueño del boleto.
 *   - Nombre del socio titular.
 * Insensible a mayúsculas y acentos. Requiere ≥2 caracteres.
 */
export const buscarAsistentes = async (eventoId, query) => {
  const q = (query || "").trim();
  if (!eventoId || q.length < 2 || !haySesionRecepcion()) return [];
  try {
    const { data } = await api.get("/recepcion/buscar", {
      params: { eventoId, q },
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

// Alias legacy — mantiene compatibilidad con imports previos.
export const buscarBoletosPorNombre = buscarAsistentes;

/* -------------------- Bitácora de accesos -------------------- */

/**
 * Bitácora de accesos del evento (append-only, solo lectura).
 * Backend (`GET /api/recepcion/accesos?eventoId`) enriquece cada fila con
 * `modalidad`, `mesa` (MESAS) y `salon` (ACCESO_GENERAL).
 */
export const listarAccesos = async (eventoId) => {
  if (!eventoId || !haySesionRecepcion()) return [];
  try {
    const { data } = await api.get("/recepcion/accesos", {
      params: { eventoId },
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/* -------------------- Validación -------------------- */

const _fallbackError = () => ({
  resultado: "RECHAZADO",
  motivo: "No se pudo comunicar con el servidor de recepción.",
  boleto: null,
});

export const validarBoletoPorQR = async (qrToken, opciones = {}) => {
  const { eventoId = null, forzar = false } = opciones;
  if (!haySesionRecepcion()) return _fallbackError();
  try {
    const { data } = await api.post("/recepcion/validar-qr", {
      qrToken: qrToken || "",
      eventoId,
      forzar: !!forzar,
    });
    return data;
  } catch {
    return _fallbackError();
  }
};

export const validarBoletoManual = async (boletoId, opciones = {}) => {
  const { eventoId = null, forzar = false } = opciones;
  if (!haySesionRecepcion()) return _fallbackError();
  try {
    const { data } = await api.post("/recepcion/validar-manual", {
      boletoId,
      eventoId,
      forzar: !!forzar,
    });
    return data;
  } catch {
    return _fallbackError();
  }
};
